import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import {
  productQueryOptions,
  useGetAllArticlesQuery,
  useGetArticleByHandleQuery,
} from "@modules/shopify/hooks";
import { PageBreadcrumb } from "@modules/shared/Breadcrumbs/PageBreadcrumb";
import { ArticleHeader } from "@modules/shop/components/Blog/ArticleHeader";
import { ArticleCta } from "@modules/shop/components/Blog/ArticleCta";
import { ArticleGrid } from "@modules/shop/components/Blog/ArticleGrid";
import { BlogProductCards } from "@modules/shop/components/Blog/BlogProductCards";
import {
  articleProductHandles,
  splitArticleBody,
} from "@modules/shop/components/Blog/articleBody";
import { getCountryVariables } from "@/modules/shared/localization/const/countries";

interface BlogArticlePageViewProps {
  blogHandle: string;
  articleHandle: string;
  locale: string;
}

export function BlogArticlePageView({
  blogHandle,
  articleHandle,
  locale,
}: Readonly<BlogArticlePageViewProps>) {
  const { t } = useTranslation();
  const localeVars = getCountryVariables(locale);

  const { data } = useGetArticleByHandleQuery({
    blogHandle,
    articleHandle,
    ...localeVars,
  });

  const article = data?.blog?.articleByHandle;
  const contentHtml =
    typeof article?.contentHtml === "string" ? article.contentHtml : "";
  const products = useQueries({
    queries: articleProductHandles(contentHtml).map((handle) =>
      productQueryOptions({ handle, ...localeVars }),
    ),
    combine: (results) =>
      results.flatMap((result) =>
        result.data?.product ? [result.data.product] : [],
      ),
  });

  if (!article) {
    return <ArticleNotFound locale={locale} />;
  }

  const readingMinutes = Math.max(
    1,
    Math.round(contentHtml.replace(/<[^>]+>/g, " ").split(/\s+/).length / 200),
  );

  return (
    <div className="min-h-screen">
      <article className="mx-auto max-w-6xl px-4 pt-8 pb-20 sm:px-6 lg:px-8">
        <PageBreadcrumb
          className="mb-10 max-mobile:mb-8"
          segments={[
            {
              label: t("Blog.breadcrumbs.home"),
              to: "/$locale",
              params: { locale },
            },
            {
              label: "Blog",
              to: "/$locale/blog",
              params: { locale },
            },
            { label: article.title },
          ]}
        />

        <ArticleHeader
          title={article.title}
          publishedAt={article.publishedAt}
          locale={locale}
          readingMinutes={readingMinutes}
          authorName={article.authorV2?.name}
          excerpt={article.excerpt}
          tags={article.tags}
          imageUrl={article.image?.url}
          imageAlt={article.image?.altText}
        />

        <div className="mx-auto mt-12 max-w-[42rem]">
          {splitArticleBody(contentHtml).map((segment, index) =>
            segment.type === "products" ? (
              <BlogProductCards
                key={index}
                handles={segment.handles}
                products={products}
                fallbackHtml={segment.html}
                locale={locale}
              />
            ) : (
              <div
                key={index}
                className="blog-article-content"
                dangerouslySetInnerHTML={{ __html: segment.html }}
              />
            ),
          )}
        </div>

        <ArticleCta locale={locale} />
      </article>

      <RelatedArticles
        currentArticleId={article.id}
        locale={locale}
        localeVars={localeVars}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

export function ArticleNotFound({ locale }: Readonly<{ locale: string }>) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <h1 className="mb-2 text-xlarge font-bold text-text-1">
          {t("Blog.articleNotFound")}
        </h1>
        <Link
          to="/$locale/blog"
          params={{ locale }}
          className="text-small font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
        >
          ← {t("Blog.backToBlog")}
        </Link>
      </div>
    </div>
  );
}

function RelatedArticles({
  currentArticleId,
  locale,
  localeVars,
}: Readonly<{
  currentArticleId: string;
  locale: string;
  localeVars: ReturnType<typeof getCountryVariables>;
}>) {
  const { t } = useTranslation();

  const { data: allArticlesData } = useGetAllArticlesQuery({
    first: 6,
    ...localeVars,
  });

  const allArticles = allArticlesData?.articles.nodes ?? [];
  const relatedArticles = allArticles
    .filter((a) => a.id !== currentArticleId)
    .slice(0, 3);

  if (relatedArticles.length === 0) return null;

  return (
    <section
      aria-labelledby="related-articles"
      className="border-t border-border-5"
    >
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <h2
          id="related-articles"
          className="mb-10 text-xlarge font-bold text-balance text-text-1 max-mobile:text-large"
        >
          {t("Blog.relatedArticles")}
        </h2>
        <ArticleGrid
          articles={relatedArticles}
          locale={locale}
          headingLevel="h3"
        />
      </div>
    </section>
  );
}
